/**
 * Markdown to HTML rendering utilities for displaying content with images
 */

/**
 * Convert markdown to HTML for display
 * Handles: images, headings, bold, italic, lists, blockquotes, code, paragraphs
 */
export function markdownToHtml(md: string): string {
  if (!md) return '';

  let html = md;

  // Convert code blocks (must be before inline code)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
    const langAttr = lang ? ` class="language-${lang}"` : '';
    // Encode HTML entities in code
    const encodedCode = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    return `<pre><code${langAttr}>${encodedCode}</code></pre>`;
  });

  // Convert inline code (before other transformations)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Convert images - support both markdown syntax and existing img tags
  // Markdown syntax: ![alt](url)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg my-4" />');

  // Also handle any existing <img> tags without classes
  html = html.replace(/<img\s+src="([^"]+)"([^>]*)>/gi, (match, src, rest) => {
    if (rest.includes('class=')) {
      return match; // Already has classes
    }
    const altMatch = rest.match(/alt="([^"]*)"/);
    const alt = altMatch ? altMatch[1] : '';
    return `<img src="${src}" alt="${alt}" class="max-w-full h-auto rounded-lg my-4" />`;
  });

  // Convert headings (must be at line start)
  html = html.replace(/^### (.*?)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>');
  html = html.replace(/^## (.*?)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>');
  html = html.replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>');

  // Convert blockquotes (must be at line start)
  html = html.replace(/^> (.*?)$/gm, '<blockquote class="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-2">$1</blockquote>');

  // Convert unordered lists
  html = html.replace(/^- (.*?)$/gm, '<li class="ml-4">$1</li>');
  html = html.replace(/(<li[^>]*>.*?<\/li>\n?)+/g, (match) => `<ul class="list-disc ml-4 my-2">${match}</ul>`);

  // Convert ordered lists
  html = html.replace(/^\d+\. (.*?)$/gm, '<li class="ml-4">$1</li>');

  // Convert inline formatting
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Convert line breaks to preserve formatting
  // But don't convert within HTML tags
  const lines = html.split('\n');
  const processedLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      processedLines.push('<br />');
      continue;
    }

    // Don't wrap lines that are already HTML elements
    if (
      trimmed.startsWith('<h') ||
      trimmed.startsWith('<ul') ||
      trimmed.startsWith('<ol') ||
      trimmed.startsWith('<li') ||
      trimmed.startsWith('<blockquote') ||
      trimmed.startsWith('<img') ||
      trimmed.startsWith('<pre') ||
      trimmed.startsWith('<code') ||
      trimmed.startsWith('<br') ||
      trimmed.startsWith('</ul') ||
      trimmed.startsWith('</ol')
    ) {
      processedLines.push(trimmed);
    } else {
      processedLines.push(`<p class="my-1">${trimmed}</p>`);
    }
  }

  return processedLines.join('');
}

/**
 * Sanitize HTML to prevent XSS attacks while allowing safe elements
 * Only allows a whitelist of tags and attributes
 */
export function sanitizeHtml(html: string): string {
  // Define allowed tags and their allowed attributes
  const allowedTags: Record<string, string[]> = {
    p: ['class'],
    br: [],
    strong: [],
    em: [],
    h1: ['class'],
    h2: ['class'],
    h3: ['class'],
    ul: ['class'],
    ol: ['class'],
    li: ['class'],
    blockquote: ['class'],
    code: ['class'],
    pre: ['class'],
    img: ['src', 'alt', 'class', 'width', 'height'],
    a: ['href', 'title', 'class'],
  };

  // Create a temporary element to parse HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');

  function sanitizeNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Check if tag is allowed
      if (!allowedTags[tagName]) {
        // Return only the text content of disallowed tags
        return element.textContent || '';
      }

      // Build allowed attributes
      const allowedAttrs = allowedTags[tagName];
      const attrs: string[] = [];

      for (const attr of allowedAttrs) {
        const value = element.getAttribute(attr);
        if (value !== null) {
          // Validate src attribute for images - only allow data URLs, http, https
          if (attr === 'src') {
            if (!value.startsWith('data:') && !value.startsWith('http://') && !value.startsWith('https://') && !value.startsWith('/')) {
              continue; // Skip invalid src
            }
          }
          // Validate href for links
          if (attr === 'href') {
            if (!value.startsWith('http://') && !value.startsWith('https://') && !value.startsWith('/') && !value.startsWith('#')) {
              continue; // Skip invalid href
            }
          }
          attrs.push(`${attr}="${value.replace(/"/g, '&quot;')}"`);
        }
      }

      // Build the sanitized element
      const attrStr = attrs.length > 0 ? ' ' + attrs.join(' ') : '';
      const childContent = Array.from(element.childNodes).map(sanitizeNode).join('');

      // Self-closing tags
      if (['br', 'img'].includes(tagName)) {
        return `<${tagName}${attrStr} />`;
      }

      return `<${tagName}${attrStr}>${childContent}</${tagName}>`;
    }

    return '';
  }

  return Array.from(doc.body.childNodes).map(sanitizeNode).join('');
}

/**
 * Convert markdown to sanitized HTML ready for dangerouslySetInnerHTML
 */
export function renderMarkdown(markdown: string): string {
  const html = markdownToHtml(markdown);
  return sanitizeHtml(html);
}
